import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getHistory } from '../../../../../../../common/HistoryDrawer/HistoryDrawerApiHelper';
import { resolveUserName } from '../../../../../../../common/HistoryDrawer/useHistoryDrawer';
import { DATE_FORMATS, formatDateByFormat } from '../../../../../../../utils/dateUtils';
import { UseManagementUserProfileLogicProps } from './ManagementUserProfile.types';

/**
 * Audit trail and edit-modal state for the director drawer, so the component itself stays
 * composition and JSX.
 */
export function useManagementUserProfileLogic({
    managementProfileId,
    assesseeOrgId,
    isPDFReport = false,
    onEditSave,
}: UseManagementUserProfileLogicProps) {
    // Monotonic token identifying the newest load; anything older discards its result.
    const latestLoadRef = useRef(0);
    const [lastAudit, setLastAudit] = useState<{ changeUserId: string; changeDate: string | number } | null>(null);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const ownerList = useSelector((state: any) => state?.investor?.ownerDropdownOptions) ?? [];

    /**
     * Newest audit row for this director. `fieldKey` is deliberately omitted: AuditLogService
     * treats it as optional and returns the whole trail for the referenceId ascending by
     * changeDate, so the last row is the most recent edit to ANY field — one request instead of
     * one per editable field.
     */
    const loadLastAudit = useCallback(async () => {
        if (isPDFReport || !managementProfileId || !assesseeOrgId) return;

        const token = ++latestLoadRef.current;

        const result = await getHistory({
            referenceId: managementProfileId,
            key: '',
            assesseeOrgId,
            historySource: 'MANAGEMENT_PROFILE',
        });

        if (token !== latestLoadRef.current) return;

        if (result === false) {
            console.error('Management profile audit trail failed to load');
            setLastAudit(null);
            return;
        }

        if (!result.length) {
            setLastAudit(null);
            return;
        }

        const newest = result[result.length - 1];
        setLastAudit({ changeUserId: newest.changeUserId, changeDate: newest.changeDate });
    }, [managementProfileId, assesseeOrgId, isPDFReport]);

    useEffect(() => {
        // Cleared up front: the drawer is reused across directors, so between the switch and the
        // new response landing the header would otherwise still show the previous director's line.
        setLastAudit(null);
        loadLastAudit();
    }, [loadLastAudit]);

    /**
     * Wraps the caller's save so a successful edit refreshes the trail — the edit the user just
     * made IS the new "last updated by", and the effect above cannot see it because it keys on
     * the director, which has not changed.
     */
    const handleEditSave = useCallback(
        async (updates: Record<string, string>) => {
            const saved = await onEditSave?.(updates);
            if (saved) await loadLastAudit();
            return !!saved;
        },
        [onEditSave, loadLastAudit],
    );

    const lastUpdatedLabel = lastAudit
        ? `Last updated by ${resolveUserName(lastAudit.changeUserId, ownerList)} on ${formatDateByFormat(
              lastAudit.changeDate,
              DATE_FORMATS.DATE_FORMAT_DD_MMM_YYYY,
          )}`
        : '';

    return { lastUpdatedLabel, isEditModalOpen, setEditModalOpen, handleEditSave };
}
